import { Box, Switch, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { apiGet, apiPost } from "../../services/api";
import { colors } from "../../theme";

interface CurrentUser {
    id: number;
    username: string;
    email: string;
    perProviderSongState: boolean;
}

export default function PreferencesPanel() {
    const [perProvider, setPerProvider] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        apiGet<CurrentUser>("/v1/users/me")
            .then(user => { if (!cancelled) setPerProvider(user.perProviderSongState); })
            .catch(() => { if (!cancelled) setError("Couldn't load your preferences"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const handleToggle = async (next: boolean) => {
        const previous = perProvider;
        setPerProvider(next);
        setSaving(true);
        setError(null);
        try {
            await apiPost("/v1/users/preferences", { perProviderSongState: next });
        } catch (err: any) {
            setPerProvider(previous);
            setError(err?.message || "Couldn't save that preference");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ px: { xs: 3, md: 6 }, py: { xs: 4, md: 6 }, maxWidth: 760 }}>
            <Typography variant="h3" sx={{ mb: 1 }}>Preferences</Typography>
            <Typography variant="body1" sx={{ color: colors.textSecondary, mb: 5 }}>
                How Shufl behaves across the streaming services you've connected.
            </Typography>

            <Box
                sx={{
                    borderRadius: 3,
                    border: `1px solid ${colors.line}`,
                    backgroundColor: colors.elevated,
                    px: 3,
                    py: 2.5,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2.5,
                }}
            >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="h6">Track song status per service</Typography>
                    <Typography sx={{ fontSize: 13, color: colors.textMuted, mt: 0.5 }}>
                        Off, a song's status is shared across every service you've linked - skipping
                        a track skips it everywhere. On, each service keeps its own status, so you
                        can keep a song on one and not the other.
                    </Typography>
                    {error && (
                        <Typography sx={{ fontSize: 12, color: colors.danger, mt: 1 }}>{error}</Typography>
                    )}
                </Box>

                <Switch
                    checked={perProvider}
                    disabled={loading || saving}
                    onChange={(event) => handleToggle(event.target.checked)}
                />
            </Box>
        </Box>
    );
}
